import React, { useCallback, useEffect } from 'react';
import { Drawer, Button, Empty } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from 'sama-shared';
import type { RootState } from '../../../types/store';
import type { ICartItem } from '../types/storefront.types';
import {
  setCartDrawerOpen,
  updateCartItemQuantity,
  removeCartItem,
  clearCart,
  selectIsStoreOpen,
  selectCanOrder,
  selectComboCart,
  selectCartTotalElements,
  updateComboCartItemQuantity,
  removeComboCartItem,
} from '../slice/storefrontSlice';
import { useCartPricing } from '../hooks/useCartPricing';
import { usePrice } from '../hooks/usePrice';
import { CartLineItem } from './cart/CartLineItem';
import { ComboCartLineItem } from './cart/ComboCartLineItem';
import { CartSummary } from './cart/CartSummary';
import { cartStyles as s } from './cart/cart.styles';

export const CartDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);

  const isOpen = useSelector((state: RootState) => state.storefront.isCartDrawerOpen);
  const cart = useSelector((state: RootState): ICartItem[] => state.storefront.cart);
  const comboCart = useSelector(selectComboCart);
  const totalElements = useSelector(selectCartTotalElements);
  const isEmpty = cart.length === 0 && comboCart.length === 0;
  const subdomain = useSelector((state: RootState) => state.storefront.subdomain);
  const isStoreOpen = useSelector(selectIsStoreOpen);

  const canOrder = useSelector(selectCanOrder);
  const price = usePrice();
  const pricing = useCartPricing(cart, comboCart);

  const isOnCheckout = location.pathname.includes('/checkout');

  // Si el carrito queda vacio estando en checkout, cerrar el drawer para dejar ver EmptyCartView.
  useEffect(() => {
    if (isEmpty && isOpen && isOnCheckout) {
      const timer = setTimeout(() => dispatch(setCartDrawerOpen(false)), 120);
      return () => clearTimeout(timer);
    }
  }, [isEmpty, isOpen, isOnCheckout, dispatch]);

  const handleChangeQuantity = useCallback(
    (cartItemId: string, quantity: number) => {
      dispatch(updateCartItemQuantity({ cartItemId, quantity }));
    },
    [dispatch]
  );

  const handleRemove = useCallback(
    (cartItemId: string) => {
      dispatch(removeCartItem(cartItemId));
    },
    [dispatch]
  );

  const handleChangeComboQuantity = useCallback(
    (cartItemId: string, quantity: number) => {
      dispatch(updateComboCartItemQuantity({ cartItemId, quantity }));
    },
    [dispatch]
  );

  const handleRemoveCombo = useCallback(
    (cartItemId: string) => {
      dispatch(removeComboCartItem(cartItemId));
    },
    [dispatch]
  );

  const handleGoToCheckout = () => {
    if (!isStoreOpen || !canOrder || !subdomain) return;
    dispatch(setCartDrawerOpen(false));
    navigate(`/store/${subdomain}/checkout`);
  };

  const handleClearCart = () => {
    dispatch(clearCart());
    dispatch(setCartDrawerOpen(false));
    if (isOnCheckout) {
      const target = subdomain ? `/store/${subdomain}` : '/';
      setTimeout(() => navigate(target), 180);
    }
  };

  return (
    <Drawer
      title={
        <div style={s.headerRow}>
          <h2 style={s.headerTitle}>
            Tu pedido
            {!isEmpty && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--color-muted)',
                  marginLeft: 8,
                }}
              >
                {totalElements}
              </span>
            )}
          </h2>
          {!isEmpty && (
            <Button
              type="text"
              size="small"
              onClick={handleClearCart}
              style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
                fontSize: 13,
                color: 'var(--color-error)',
              }}
            >
              Vaciar
            </Button>
          )}
        </div>
      }
      placement={isMobile ? 'bottom' : 'right'}
      // Ancho fijo de 400px desbordaba en telefonos de 360-390px.
      width={isMobile ? '100%' : 400}
      onClose={() => dispatch(setCartDrawerOpen(false))}
      open={isOpen}
      styles={{
        body: s.drawerBody,
        header: s.drawerHeader,
        ...(isMobile ? { wrapper: s.sheetWrapper } : {}),
      }}
    >
      {isEmpty ? (
        <div style={s.emptyState}>
          <Empty description="Carrito vacío" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <>
          <div style={s.scrollArea} className="hide-scrollbar">
            {comboCart.map((item) => (
              <ComboCartLineItem
                key={item.cartItemId}
                item={item}
                pricing={pricing.byLine.get(item.cartItemId)!}
                price={price}
                onChangeQuantity={handleChangeComboQuantity}
                onRemove={handleRemoveCombo}
              />
            ))}
            {cart.map((item) => (
              <CartLineItem
                key={item.cartItemId}
                item={item}
                pricing={pricing.byLine.get(item.cartItemId)!}
                price={price}
                onChangeQuantity={handleChangeQuantity}
                onRemove={handleRemove}
              />
            ))}
          </div>

          <div
            style={{
              paddingTop: 14,
              marginTop: 4,
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <CartSummary
              pricing={pricing}
              price={price}
              isStoreOpen={isStoreOpen}
              canOrder={canOrder}
              onCheckout={handleGoToCheckout}
            />
          </div>
        </>
      )}
    </Drawer>
  );
};
